import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PacModeBanner } from "@/components/ops/PacModeBanner";
import {
  FileText, Shield, BarChart3, BookOpen, LayoutDashboard,
  AlertTriangle, CheckCircle2, Clock, TrendingUp, Calendar,
  RefreshCw, Info,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Panel", icon: LayoutDashboard, path: "/ops/fiscal" },
  { id: "facturacion", label: "Facturacion", icon: FileText, path: "/ops/fiscal/facturacion" },
  { id: "csd", label: "Certificados CSD", icon: Shield, path: "/ops/fiscal/csd" },
  { id: "declaraciones", label: "Declaraciones SAT", icon: BarChart3, path: "/ops/fiscal/declaraciones" },
  { id: "buzon", label: "Buzon tributario", icon: BookOpen, path: "/ops/fiscal/buzon" },
];

function MetricCard({ label, value, sub, icon: Icon, color = "blue", badge }) {
  const colors = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    green: "border-green-200 bg-green-50 text-green-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-2 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        {badge && (
          <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

function CsdStatusBadge({ status, days }) {
  if (status === "active" && days > 30) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">Vigente ({days}d)</span>;
  }
  if (status === "active" && days <= 30) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">Por vencer ({days}d)</span>;
  }
  if (status === "expired") {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">Vencido</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{status}</span>;
}

export default function OpsFiscalDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeTab = TABS.find(t => {
    if (t.id === "dashboard") return location.pathname === "/ops/fiscal";
    return location.pathname.startsWith(t.path);
  })?.id || "dashboard";

  const load = useCallback(async () => {
    try {
      const res = await api.get("/ops/fiscal/dashboard");
      setData(res.data);
    } catch (e) {
      toast.error("Error al cargar el panel fiscal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTab = (tab) => {
    navigate(tab.path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-xl font-bold text-slate-900">Modulo Fiscal</h1>
          <p className="text-sm text-slate-500">CFDI 4.0 — SAT Mexico</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6">
          <nav className="flex gap-0 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  data-testid={`fiscal-tab-${tab.id}`}
                  onClick={() => handleTab(tab)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Panel content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6"><PacModeBanner /></div>
        {loading ? (
          <div className="flex items-center justify-center h-48 text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            Cargando panel fiscal...
          </div>
        ) : !data ? (
          <div className="text-center text-slate-500 py-12">No se pudo cargar el panel</div>
        ) : (
          <div className="space-y-8">
            {/* Metricas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label="Facturas emitidas (mes)"
                value={data.facturas_emitidas?.count ?? 0}
                sub={`$${(data.facturas_emitidas?.total ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`}
                icon={FileText}
                color="blue"
              />
              <MetricCard
                label="Pendientes validacion SAT"
                value={data.facturas_recibidas_pendientes_sat ?? 0}
                sub="Facturas de proveedor"
                icon={Clock}
                color={data.facturas_recibidas_pendientes_sat > 0 ? "amber" : "green"}
              />
              <MetricCard
                label="CSDs registrados"
                value={data.csds?.length ?? 0}
                sub="Certificados de sello"
                icon={Shield}
                color="slate"
              />
              <MetricCard
                label="Alertas fiscales"
                value={data.alertas_fiscales_activas ?? 0}
                sub="Sin leer"
                icon={AlertTriangle}
                color={data.alertas_fiscales_activas > 0 ? "red" : "green"}
              />
            </div>

            {/* CSDs */}
            {data.csds && data.csds.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-700 mb-4">Certificados de Sello Digital (CSD)</h2>
                <div className="space-y-3">
                  {data.csds.map(csd => (
                    <div key={csd.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{csd.legal_name || csd.rfc}</p>
                        <p className="text-xs text-slate-500">RFC: {csd.rfc} — Vence: {csd.expiration_date}</p>
                      </div>
                      <CsdStatusBadge status={csd.status} days={csd.days_until_expiry} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate("/ops/fiscal/csd")}
                  className="mt-3 text-xs text-blue-600 hover:underline"
                >
                  Administrar CSDs
                </button>
              </div>
            )}

            {/* Periodo */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">Periodo actual: {data.period?.from} al {data.period?.to}</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  El modulo fiscal usa PAC en modo <strong>{process.env.REACT_APP_BACKEND_URL?.includes("preview") ? "mock/demo" : "produccion"}</strong>.
                  Para activar el timbrado real, configura PAC_PROVIDER=solucion_factible en las Custom Keys de Emergent.
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                data-testid="btn-nueva-factura"
                onClick={() => navigate("/ops/fiscal/facturacion/nueva")}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl p-4 text-left transition-colors"
              >
                <FileText className="w-5 h-5 mb-2" />
                <p className="font-semibold text-sm">Nueva factura</p>
                <p className="text-xs text-blue-200">Emitir CFDI 4.0</p>
              </button>
              <button
                data-testid="btn-declaraciones"
                onClick={() => navigate("/ops/fiscal/declaraciones")}
                className="bg-slate-800 hover:bg-slate-900 text-white rounded-xl p-4 text-left transition-colors"
              >
                <BarChart3 className="w-5 h-5 mb-2" />
                <p className="font-semibold text-sm">Declaraciones SAT</p>
                <p className="text-xs text-slate-400">DIOT, balanza, IVA mensual</p>
              </button>
              <button
                data-testid="btn-csd-admin"
                onClick={() => navigate("/ops/fiscal/csd")}
                className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl p-4 text-left transition-colors"
              >
                <Shield className="w-5 h-5 mb-2 text-slate-600" />
                <p className="font-semibold text-sm text-slate-800">Administrar CSDs</p>
                <p className="text-xs text-slate-500">Registrar y validar certificados</p>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
